import BackupSummary from '../../types/backup/BackupSummary';
import BackupHistoryItem from '../../types/backup/BackupHistoryItem';
import { BackupKind, formatBackupTime } from './backupUtils';

export interface ChainBackup {
    start?: string;
    duration?: number;
    size?: number;
    source?: string;
}

export function chainBackup(row: BackupSummary, kind: BackupKind): ChainBackup {
    switch (kind) {
        case 'full':
            return { start: row.fullstart, duration: row.fullduration, size: row.fullsize, source: row.fullsource };
        case 'differential':
            return {
                start: row.differentialstart,
                duration: row.differentialduration,
                size: row.differentialsize,
                source: row.differentialsource,
            };
        default:
            return { start: row.logstart, duration: row.logduration, size: row.logsize, source: row.logsource };
    }
}

// A differential only belongs to the current chain if it was taken after the
// latest full backup; otherwise it is based on an older one.
export function chainDifferential(row: BackupSummary): ChainBackup {
    const differential = chainBackup(row, 'differential');
    if (differential.start && row.fullstart &&
        Date.parse(differential.start) < Date.parse(row.fullstart)) {
        return {};
    }
    return differential;
}

type ChainStatus = Exclude<BackupSummary['chainstatus'], undefined>;

export const CHAIN_STATUS_LABELS: Record<ChainStatus, string> = {
    ok: 'OK',
    broken: 'Broken',
    unverified: 'Unverified',
    warning: 'Warning',
};

// Most serious first, for sorting.
export const CHAIN_STATUS_RANK: Record<ChainStatus, number> = {
    broken: 4,
    unverified: 3,
    warning: 2,
    ok: 1,
};

// The reason, then how far the chain restores to.
export function chainTooltip(row: BackupSummary): string {
    const lines = row.chainmessage ? [row.chainmessage] : [];
    if (row.pitrfrom || row.pitrto) {
        lines.push(`Point-in-time restore from ${formatBackupTime(row.pitrfrom)} to ${formatBackupTime(row.pitrto)}`);
    }
    if (row.verifiedsince) {
        lines.push(`The full backup is older than the history, so the chain is checked since ${
            formatBackupTime(row.verifiedsince)}`);
    }
    return lines.join('. ');
}

// What to call a break in the log chain, or count of them. Where a replica is
// not monitored the missing logs may have been backed up there, so it is only
// a possible gap.
export function chainBreakLabel(possible: boolean, count = 1): string {
    const noun = possible ? 'possible gap' : 'chain break';
    if (count > 1) {
        return `${count} ${noun}s`;
    }
    return noun.charAt(0).toUpperCase() + noun.slice(1);
}

// The full or differential backups that start a new chain after a break: for
// each break, the first one after the last good log backup.
export function newChainStarts(items: BackupHistoryItem[]): Set<BackupHistoryItem> {
    const bases = items
        .filter(item => item.status === 'success' && !item.iscopyonly && item.finishtime &&
            (item.backuptype === 'full' || item.backuptype === 'differential'))
        .sort((a, b) => Date.parse(a.finishtime!) - Date.parse(b.finishtime!));
    const result = new Set<BackupHistoryItem>();

    items.filter(item => item.chainbreak && item.chainbreakafter).forEach(item => {
        const after = Date.parse(item.chainbreakafter!);
        const base = bases.find(candidate => Date.parse(candidate.finishtime!) > after &&
            Date.parse(candidate.timeslice) <= Date.parse(item.timeslice));
        if (base) {
            result.add(base);
        }
    });

    return result;
}

// A break the summary counts as only possibly a gap: the chain is waiting on a
// replica that is not monitored, and the break is in the current chain. Older
// breaks are not judged by today's replicas.
export function isPossibleGap(item: BackupHistoryItem, row: BackupSummary): boolean {
    return row.chainreason === 'unmonitoredreplica' && !!item.chainbreakafter &&
        (!row.pitrfrom || Date.parse(item.chainbreakafter) >= Date.parse(row.pitrfrom));
}
